import React from 'react';
import { 
  DollarSign, Users, Target, Repeat, BarChart3, Clock, 
  MapPin, Zap, CheckCircle, AlertTriangle 
} from 'lucide-react';
import { formatCurrency } from '../../utils/helpers';
import { 
  TrendIndicator, ProgressBar, SimpleBarChart, CollapsibleSection, StatCard 
} from './ReportComponents';

const AnalyticsReport = ({ data, expandedSections, toggleSection }) => {
  if (!data) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p>No data available for this period.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Hero Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          label="Total Revenue"
          value={formatCurrency(data.totalRevenue)}
          subtext="incl. personal/free · vs last year"
          trend={data.yearOverYearGrowth}
          icon={DollarSign}
          color="green"
        />

        <StatCard
          label="Jobs Completed"
          value={data.totalJobsCompleted}
          subtext={`${formatCurrency(data.averageRevenuePerJob)} avg`}
          icon={CheckCircle}
          color="blue"
        />

        <StatCard
          label="New Customers"
          value={data.newCustomersThisPeriod}
          subtext="first-time this period"
          icon={Target}
          color="purple"
        />

        <StatCard
          label="Repeat Customers"
          value={`${data.repeatCustomerPercent.toFixed(0)}%`}
          subtext={`${data.repeatCustomers} of ${data.totalCustomers}`}
          icon={Repeat}
          color="amber"
        />
      </div>

      {/* Revenue Trend Chart */}
      {data.monthlyTrends && data.monthlyTrends.length > 1 && (
        <div className="card p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <BarChart3 size={18} className="text-gray-500" />
              <h3 className="font-semibold text-gray-900">Revenue Trend</h3>
            </div>
            <div className="text-sm text-gray-500">
              Best: <span className="font-medium text-green-600">{data.bestMonth}</span> ({formatCurrency(data.bestMonthRevenue)})
            </div>
          </div>
          <SimpleBarChart 
            data={data.monthlyTrends.map(m => ({ 
              label: m.monthName, 
              value: m.revenue 
            }))}
            color="bg-green-500"
          />
        </div>
      )}

      {/* Insights Grid */}
      <div className="grid lg:grid-cols-2 gap-4">
        {/* Top Customers */}
        <CollapsibleSection
          title="Top Customers"
          icon={Users}
          isExpanded={expandedSections.customers}
          onToggle={() => toggleSection('customers')}
        >
          {data.topCustomersByRevenue.length === 0 ? (
            <p className="text-gray-500 text-center py-6 text-sm">No customer data yet</p>
          ) : (
            <div className="divide-y divide-gray-50">
              {data.topCustomersByRevenue.map((customer, i) => (
                <div key={i} className="flex items-center justify-between px-3 py-2 hover:bg-gray-50">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      i === 0 ? 'bg-yellow-100 text-yellow-700' :
                      i === 1 ? 'bg-gray-100 text-gray-600' :
                      i === 2 ? 'bg-orange-100 text-orange-700' :
                      'bg-gray-50 text-gray-500'
                    }`}>
                      {i + 1}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{customer.customerName}</p>
                      <p className="text-xs text-gray-400">
                        {customer.jobCount} job{customer.jobCount !== 1 ? 's' : ''}
                        {customer.isRepeatCustomer && (
                          <span className="text-green-600 ml-1">• Repeat</span>
                        )}
                      </p>
                    </div>
                  </div>
                  <p className="font-semibold text-gray-900">{formatCurrency(customer.totalRevenue)}</p>
                </div>
              ))}
            </div>
          )}
        </CollapsibleSection>

        {/* Service Performance */}
        <CollapsibleSection
          title="Service Performance"
          icon={Zap}
          isExpanded={expandedSections.services}
          onToggle={() => toggleSection('services')}
        >
          {data.servicePerformance.length === 0 ? (
            <p className="text-gray-500 text-center py-6 text-sm">No service data yet</p>
          ) : (
            <div className="divide-y divide-gray-50">
              {data.servicePerformance.slice(0, 5).map((service, i) => (
                <div key={i} className="px-3 py-2 hover:bg-gray-50">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm font-medium text-gray-900">{service.serviceName}</p>
                    <p className="text-sm font-semibold text-gray-900">{formatCurrency(service.totalRevenue)}</p>
                  </div>
                  <ProgressBar 
                    value={service.percentOfRevenue} 
                    max={100} 
                    color="bg-primary-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    {service.jobCount} job{service.jobCount !== 1 ? 's' : ''} • {formatCurrency(service.averagePrice)} avg
                  </p>
                </div>
              ))}
            </div>
          )}
        </CollapsibleSection>
      </div>

      {/* Collection & Geographic Insights */}
      <div className="grid lg:grid-cols-2 gap-4">
        {/* Collection Stats */}
        <div className="card p-4">
          <div className="flex items-center gap-2 mb-3">
            <Clock size={16} className="text-gray-500" />
            <h3 className="font-medium text-gray-900">Collection Insights</h3>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Avg Days to Payment</span>
              <span className={`font-semibold ${
                data.averageDaysToPayment <= 14 ? 'text-green-600' :
                data.averageDaysToPayment <= 30 ? 'text-amber-600' : 'text-red-600'
              }`}>
                {data.averageDaysToPayment.toFixed(0)} days
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Outstanding</span>
              <span className="font-semibold text-amber-600">{formatCurrency(data.totalOutstanding)}</span>
            </div>
            {data.overdueAmount > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 flex items-center gap-1">
                  <AlertTriangle size={14} className="text-red-500" />
                  Overdue
                </span>
                <span className="font-semibold text-red-600">
                  {formatCurrency(data.overdueAmount)} ({data.overdueInvoices} inv)
                </span>
              </div>
            )}
            {data.potentialRevenueLost > 0 && (
              <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                <span className="text-sm text-gray-600">Quotes Not Won</span>
                <span className="font-semibold text-gray-500">{formatCurrency(data.potentialRevenueLost)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Geographic */}
        <CollapsibleSection
          title="Revenue by Area"
          icon={MapPin}
          isExpanded={expandedSections.geography}
          onToggle={() => toggleSection('geography')}
          rightContent={data.topCity && (
            <span className="text-xs text-gray-400">Top: {data.topCity}</span>
          )}
        >
          {data.revenueByCity.length === 0 ? (
            <p className="text-gray-500 text-center py-6 text-sm">No location data</p>
          ) : (
            <div className="divide-y divide-gray-50">
              {data.revenueByCity.map((city, i) => (
                <div key={i} className="px-3 py-2 hover:bg-gray-50">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm font-medium text-gray-900">
                      {city.city}{city.state && `, ${city.state}`}
                    </p>
                    <p className="text-sm font-semibold text-gray-900">{formatCurrency(city.revenue)}</p>
                  </div>
                  <ProgressBar value={city.percentOfRevenue} max={100} color="bg-indigo-500" />
                  <p className="text-xs text-gray-400 mt-1">{city.jobCount} jobs</p>
                </div>
              ))}
            </div>
          )}
        </CollapsibleSection>
      </div>

      {/* New Customer Acquisition */}
      {data.newCustomersThisPeriod > 0 && (
        <div className="card p-4 bg-gradient-to-r from-cyan-50 to-blue-50 border-cyan-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-cyan-100 rounded-full flex items-center justify-center">
              <Users size={20} className="text-cyan-600" />
            </div>
            <div>
              <h3 className="font-semibold text-cyan-900">
                {data.newCustomersThisPeriod} New Customer{data.newCustomersThisPeriod !== 1 ? 's' : ''}
              </h3>
              <p className="text-sm text-cyan-700">
                You've acquired {data.newCustomersThisPeriod} new customers this period
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsReport;